import React, { useState } from "react";
import { Stack, Alert, Typography, Tabs, Tab } from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";

import { DashboardHeader } from "../shared";

import PanelPopulationOverview from "./PanelPopulationOverview";
import PanelCostAnalysis from "./PanelCostAnalysis";
import PanelQualityMeasures from "./PanelQualityMeasures";
import PanelCenterPerformance from "./PanelCenterPerformance";
import PanelTrends from "./PanelTrends";
import PanelCodingRevenue from "./PanelCodingRevenue";
import PanelEngagement from "./PanelEngagement";
import PanelTeamPerformance from "./PanelTeamPerformance";
import NewMembersDialog from "./NewMembersDialog";

import useCEOData from "../../hooks/useCEOData";
import { CEO_HELP_CONTENT } from "../../constants/ceoHelpContent";

/**
 * CEO Executive Dashboard v2 — Orchestrator
 * Vista ejecutiva de solo lectura con 7 paneles de KPIs + tab de Team Performance.
 */
export default function CEODashboard() {
  const {
    population,
    cost,
    quality,
    centerPerformance,
    trends,
    coding,
    engagement,
    performance,
    loading,
    error,
    loadNewMembers,
    loadAllMeasures,
    loadEmployees,
    loadClinicalPerformance,
  } = useCEOData();

  const [activeTab, setActiveTab] = useState(0);
  const [newMembersDialogOpen, setNewMembersDialogOpen] = useState(false);

  return (
    <Stack spacing={2.5}>
      {/* Header */}
      <DashboardHeader
        title="Executive Dashboard"
        subtitle="Population health metrics, financial performance, and quality measures across all centers"
        icon={<BusinessIcon />}
        color="success"
      />

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ borderRadius: 3 }}>
          <Typography fontWeight={600}>Something went wrong</Typography>
          <Typography variant="body2">{String(error)}</Typography>
        </Alert>
      )}

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.9rem",
          },
        }}
      >
        <Tab icon={<BusinessIcon />} iconPosition="start" label="Dashboard" />
        <Tab icon={<LocalHospitalIcon />} iconPosition="start" label="Clinical Performance" />
      </Tabs>

      {/* Tab 0: Main Dashboard */}
      {activeTab === 0 && (
        <Stack spacing={2.5}>
          {/* Panel 1: Population Overview */}
          <PanelPopulationOverview
            data={population}
            loading={loading.population}
            helpContent={CEO_HELP_CONTENT.populationOverview}
            onOpenNewMembers={() => setNewMembersDialogOpen(true)}
          />

          {/* Panel 2: Cost Analysis */}
          <PanelCostAnalysis
            data={cost}
            loading={loading.cost}
            helpContent={CEO_HELP_CONTENT.costAnalysis}
          />

          {/* Panel 3: Quality Measures */}
          <PanelQualityMeasures
            data={quality}
            loading={loading.quality}
            helpContent={CEO_HELP_CONTENT.qualityMeasures}
            loadAllMeasures={loadAllMeasures}
          />

          {/* Panel 4: Center Performance */}
          <PanelCenterPerformance
            data={centerPerformance}
            loading={loading.centerPerformance}
            helpContent={CEO_HELP_CONTENT.centerPerformance}
          />

          {/* Panel 5: Trends & Top Performers */}
          <PanelTrends
            data={trends}
            loading={loading.trends}
            helpContent={CEO_HELP_CONTENT.trends}
          />

          {/* Panel 6: Coding Revenue */}
          <PanelCodingRevenue
            data={coding}
            loading={loading.coding}
            helpContent={CEO_HELP_CONTENT.codingRevenue}
          />

          {/* Panel 7: Engagement & Growth */}
          <PanelEngagement
            data={engagement}
            loading={loading.engagement}
            helpContent={CEO_HELP_CONTENT.engagement}
          />
        </Stack>
      )}

      {/* Tab 1: Team Performance */}
      {activeTab === 1 && (
        <PanelTeamPerformance
          data={performance}
          loading={loading.performance}
          loadEmployees={loadEmployees}
          loadClinicalPerformance={loadClinicalPerformance}
        />
      )}

      {/* New Members Dialog (lazy) */}
      <NewMembersDialog
        open={newMembersDialogOpen}
        onClose={() => setNewMembersDialogOpen(false)}
        loadData={loadNewMembers}
      />
    </Stack>
  );
}
